import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mail } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { isValidPin, isValidUsername } from "@/lib/kidAuth";
import { Toaster, toast } from "sonner";

const TERMS_URL = "https://www.meetmori.app/en-GB/terms-of-service";
const PRIVACY_URL = "https://www.meetmori.app/en-GB/terms-of-service";

type Mode = "signin" | "signup" | "reset" | "set-password" | "kid";
type FieldHint = { text: string; isError: boolean } | null;

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Survives the Google OAuth same-origin redirect. Email-confirm flows open
// a fresh tab, so we also pass the referrer_id via signUp metadata.
const REFERRAL_STORAGE_KEY = "mori.referrer_id";
const OAUTH_INTENT_KEY = "mori.oauth_intent";
const OAUTH_ERROR_KEY = "mori.oauth_error";

function readStashedReferrerId(): string | null {
  try {
    const v = sessionStorage.getItem(REFERRAL_STORAGE_KEY);
    return v && UUID_REGEX.test(v) ? v : null;
  } catch {
    return null;
  }
}

const AuthPage = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [name, setName] = useState("");
  const [nameHint, setNameHint] = useState<FieldHint>(null);
  const [emailHint, setEmailHint] = useState<FieldHint>(null);
  const [passwordHint, setPasswordHint] = useState<FieldHint>(null);
  const [confirmHint, setConfirmHint] = useState<FieldHint>(null);
  const [linkError, setLinkError] = useState<"expired" | "generic" | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [usernameHint, setUsernameHint] = useState<FieldHint>(null);
  const [pinHint, setPinHint] = useState<FieldHint>(null);
  const [kidHelpOpen, setKidHelpOpen] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Capture ?ref=<uuid> into sessionStorage so both Google OAuth
  // (same-origin redirect) and the email-confirm round-trip have a
  // chance to resolve the referrer after auth lands. signUp also stuffs
  // the id into user_metadata as a more durable second channel.
  // Also handle ?mode=signin|signup to deep-link to a specific auth mode.
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const ref = search.get("ref");
    const modeParam = search.get("mode");

    if (modeParam === "signin" || modeParam === "signup") {
      setMode(modeParam);
    }

    if (ref && UUID_REGEX.test(ref)) {
      try {
        sessionStorage.setItem(REFERRAL_STORAGE_KEY, ref);
      } catch {
        /* storage disabled — sign-up metadata is the fallback */
      }
      search.delete("ref");
      const qs = search.toString();
      const clean = window.location.pathname + (qs ? `?${qs}` : "");
      window.history.replaceState(null, "", clean);
    }
  }, []);

  // If App.tsx detected a new user trying to sign in with Google, it stores this flag
  // before signing them out. We read it here to show the error and switch to signup.
  useEffect(() => {
    try {
      const err = localStorage.getItem(OAUTH_ERROR_KEY);
      if (err === "no_account") {
        localStorage.removeItem(OAUTH_ERROR_KEY);
        setMode("signup");
        toast.error(t("auth.signIn.googleNoAccount"));
      }
    } catch { /* storage disabled */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect expired / invalid magic link or password-reset redirect from Supabase hash
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace("#", "?"));
    const error = hash.get("error");
    const errorDesc = hash.get("error_description");
    const type = hash.get("type");

    if (type === "recovery") {
      setMode("set-password");
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    if (error && errorDesc) {
      if (
        errorDesc.toLowerCase().includes("expired") ||
        errorDesc.toLowerCase().includes("invalid")
      ) {
        setLinkError("expired");
      } else {
        setLinkError("generic");
      }
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  // Auto-focus email input when an error is shown
  useEffect(() => {
    if (linkError) {
      emailInputRef.current?.focus();
    }
  }, [linkError]);

  // Map known Supabase error messages to user-friendly copy
  const mapError = (message: string): string => {
    const m = message.toLowerCase();
    if (m.includes("invalid login credentials")) return t("auth.signIn.errorInvalidCredentials");
    if (m.includes("already registered") || m.includes("user already registered")) return t("auth.signIn.errorAlreadyRegistered");
    if (m.includes("email not confirmed")) return t("auth.signIn.errorEmailNotConfirmed");
    if (m.includes("password should be at least")) return t("auth.signIn.errorPasswordTooShort");
    if (m.includes("email rate limit") || m.includes("over_email_send_rate_limit")) return t("auth.signIn.errorRateLimit");
    return message;
  };

  const validateName = (): boolean => {
    if (name.trim().length < 3) {
      setNameHint({ text: t("auth.signIn.nameHintEmpty"), isError: true });
      return false;
    }
    setNameHint(null);
    return true;
  };

  // Field validators — return true if valid, set inline hint and return false if not
  const validateEmail = (): boolean => {
    if (!email) {
      setEmailHint({ text: t("auth.signIn.emailHintEmpty"), isError: true });
      return false;
    }
    if (!isValidEmail(email)) {
      setEmailHint({ text: t("auth.signIn.emailHintError"), isError: true });
      return false;
    }
    setEmailHint(null);
    return true;
  };

  const validatePassword = (minLength = 1): boolean => {
    if (!password) {
      setPasswordHint({ text: t("auth.signIn.passwordHintEmpty"), isError: true });
      return false;
    }
    if (password.length < minLength) {
      setPasswordHint({ text: t("auth.signIn.passwordHintTooShort"), isError: true });
      return false;
    }
    setPasswordHint(null);
    return true;
  };

  const validateConfirm = (): boolean => {
    if (confirmPassword !== password) {
      setConfirmHint({ text: t("auth.signIn.passwordHintMismatch"), isError: true });
      return false;
    }
    setConfirmHint(null);
    return true;
  };

  const requireAgreement = (): boolean => {
    if (mode !== "signup") return true;
    if (!agreedToTerms) {
      toast.error(t("auth.signIn.termsRequired"));
      return false;
    }
    return true;
  };

  const handleGoogleSignIn = async () => {
    try { localStorage.setItem(OAUTH_INTENT_KEY, "signin"); } catch { /* storage disabled */ }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      try { localStorage.removeItem(OAUTH_INTENT_KEY); } catch { /* storage disabled */ }
      toast.error(mapError(error.message));
    }
  };

  const handleGoogleSignUp = async () => {
    if (!agreedToTerms) {
      toast.error(t("auth.signIn.termsRequired"));
      return;
    }
    try { localStorage.setItem(OAUTH_INTENT_KEY, "signup"); } catch { /* storage disabled */ }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      try { localStorage.removeItem(OAUTH_INTENT_KEY); } catch { /* storage disabled */ }
      toast.error(mapError(error.message));
    }
  };

  const handleSignIn = async () => {
    const emailOk = validateEmail();
    const passwordOk = validatePassword();
    if (!emailOk || !passwordOk) return;
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      Sentry.captureException(error, { tags: { flow: "auth-password-signin" } });
      toast.error(mapError(error.message));
    }
  };

  const handleSignUp = async () => {
    const nameOk = validateName();
    const emailOk = validateEmail();
    const passwordOk = validatePassword(6);
    const confirmOk = validateConfirm();
    if (!nameOk || !emailOk || !passwordOk || !confirmOk) return;
    if (!requireAgreement()) return;
    setLoading(true);
    const referrerId = readStashedReferrerId();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: name.trim(),
          ...(referrerId ? { referrer_id: referrerId } : {}),
        },
      },
    });
    setLoading(false);
    if (error) {
      Sentry.captureException(error, { tags: { flow: "auth-password-signup" } });
      toast.error(mapError(error.message));
    } else {
      toast.success(t("auth.signIn.signUpConfirmEmail"));
    }
  };

  const handleReset = async () => {
    if (!validateEmail()) return;
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/auth",
    });
    setLoading(false);
    if (error) {
      Sentry.captureException(error, { tags: { flow: "auth-password-reset" } });
      toast.error(mapError(error.message));
    } else {
      setResetSent(true);
    }
  };

  const handleSetPassword = async () => {
    if (!validatePassword(6)) return;
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      Sentry.captureException(error, { tags: { flow: "auth-set-password" } });
      toast.error(mapError(error.message));
    } else {
      toast.success(t("auth.signIn.setPasswordSuccess"));
    }
  };

  const handleKidSignIn = async () => {
    const u = username.trim().toLowerCase();
    const p = pin.trim();
    let ok = true;
    if (!u) {
      setUsernameHint({ text: t("auth.kid.usernameEmpty"), isError: true });
      ok = false;
    } else if (!isValidUsername(u)) {
      setUsernameHint({ text: t("auth.kid.usernameInvalid"), isError: true });
      ok = false;
    }
    if (!isValidPin(p)) {
      setPinHint({ text: t("auth.kid.pinInvalid"), isError: true });
      ok = false;
    }
    if (!ok) return;

    setLoading(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke(
        "kid-signin",
        { body: { username: u, pin: p } },
      );
      if (invokeErr) throw invokeErr;
      const resp = data as {
        success?: boolean;
        errorCode?: "wrong-creds" | "locked" | "internal";
        attemptsRemaining?: number;
        unlocksAt?: string;
        session?: { access_token: string; refresh_token: string };
      };

      if (resp.success && resp.session) {
        const { error: setErr } = await supabase.auth.setSession({
          access_token: resp.session.access_token,
          refresh_token: resp.session.refresh_token,
        });
        if (setErr) throw setErr;
        // Session is set — app shell will route on auth change.
        return;
      }

      if (resp.errorCode === "locked") {
        const mins = resp.unlocksAt
          ? Math.max(1, Math.ceil((new Date(resp.unlocksAt).getTime() - Date.now()) / 60_000))
          : 15;
        toast.error(t("auth.kid.locked", { minutes: mins }));
      } else if (resp.errorCode === "wrong-creds") {
        if (typeof resp.attemptsRemaining === "number") {
          toast.error(t("auth.kid.wrongCredsRemaining", { count: resp.attemptsRemaining }));
        } else {
          toast.error(t("auth.kid.wrongCreds"));
        }
      } else {
        toast.error(t("auth.kid.wrongCreds"));
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { flow: "auth-kid-signin" } });
      toast.error(t("auth.kid.wrongCreds"));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setNameHint(null);
    setEmailHint(null);
    setPasswordHint(null);
    setConfirmHint(null);
    setUsernameHint(null);
    setPinHint(null);
    setName("");
    setPassword("");
    setConfirmPassword("");
    setUsername("");
    setPin("");
    setResetSent(false);
  };

  const hintColor = (hint: FieldHint) =>
    hint?.isError ? "#B23A3A" : "#C45B4B";

  const fieldBorder = (hint: FieldHint) =>
    hint?.isError ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-6 animate-page-enter">
      {/* Decorative organic shapes */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-primary/[0.06] animate-float" />
        <div className="absolute top-1/3 -left-32 h-96 w-96 rounded-full bg-accent/[0.07] animate-float-slow" />
        <div className="absolute -bottom-16 right-10 h-56 w-56 rounded-full bg-primary/[0.04] animate-float-slower" />
        <div className="absolute bottom-1/4 left-1/4 h-40 w-40 rounded-[40%_60%_55%_45%/50%_40%_60%_50%] bg-accent/[0.05] animate-float-slow" />
      </div>

      <div className="relative z-10 w-full max-w-sm text-center">
        <img
          src="/mori-logo.webp"
          alt="Mori"
          className="mx-auto w-full"
        />

        {/* Title / tagline */}
        {mode === "reset" ? (
          <div className="mt-3 space-y-0.5">
            <p className="text-base font-medium text-foreground">{t("auth.signIn.resetTitle")}</p>
            <p className="text-sm text-muted-foreground">{t("auth.signIn.resetSub")}</p>
          </div>
        ) : mode === "set-password" ? (
          <p className="mt-3 text-base text-muted-foreground">{t("auth.signIn.newPasswordTitle")}</p>
        ) : linkError ? (
          <div className="mt-3 space-y-0.5">
            <p className="text-base font-medium" style={{ color: "#B23A3A" }}>
              {linkError === "expired" ? t("auth.signIn.linkExpired") : t("auth.signIn.linkError")}
            </p>
            <p className="text-sm text-muted-foreground">
              {linkError === "expired" ? t("auth.signIn.linkExpiredSub") : t("auth.signIn.linkErrorSub")}
            </p>
          </div>
        ) : (
          <p className="mt-3 text-base text-muted-foreground">
            {mode === "signup" ? t("auth.taglineSignup") : t("auth.taglineSignin")}
          </p>
        )}

        {/* Kid sign-in (username + PIN) */}
        {mode === "kid" ? (
          <div className="mt-10 space-y-3 animate-page-enter">
            <p className="text-sm text-muted-foreground">{t("auth.kid.subtext")}</p>
            <div className="space-y-1">
              <Input
                autoFocus
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={t("auth.kid.usernamePlaceholder")}
                value={username}
                onChange={(e) => { setUsername(e.target.value); setUsernameHint(null); }}
                onKeyDown={(e) => e.key === "Enter" && handleKidSignIn()}
                className={`h-12 text-base bg-card ${fieldBorder(usernameHint)}`}
              />
              {usernameHint && (
                <p className="text-[13px] px-1 text-left" style={{ color: hintColor(usernameHint) }}>
                  {usernameHint.text}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                placeholder={t("auth.kid.pinPlaceholder")}
                value={pin}
                onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 4)); setPinHint(null); }}
                onKeyDown={(e) => e.key === "Enter" && handleKidSignIn()}
                className={`h-12 text-base bg-card text-center tracking-[0.5em] ${fieldBorder(pinHint)}`}
              />
              {pinHint && (
                <p className="text-[13px] px-1 text-left" style={{ color: hintColor(pinHint) }}>
                  {pinHint.text}
                </p>
              )}
            </div>
            <Button
              className="w-full h-12 text-base font-medium"
              onClick={handleKidSignIn}
              disabled={loading}
            >
              {loading ? t("auth.signIn.signInLoading") : t("auth.kid.signInButton")}
            </Button>
            <div className="flex items-baseline justify-between pt-1">
              <button
                onClick={() => switchMode("signin")}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("auth.kid.backToAdult")}
              </button>
              <button
                onClick={() => setKidHelpOpen(true)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("auth.kid.forgot")}
              </button>
            </div>
          </div>
        ) : mode === "set-password" ? (
          <div className="mt-10 space-y-3 animate-page-enter">
            <div className="space-y-1">
              <Input
                type="password"
                placeholder={t("auth.signIn.newPasswordPlaceholder")}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordHint(null); }}
                onKeyDown={(e) => e.key === "Enter" && handleSetPassword()}
                className={`h-12 text-base bg-card ${fieldBorder(passwordHint)}`}
              />
              {passwordHint && (
                <p className="text-[13px] px-1" style={{ color: hintColor(passwordHint) }}>
                  {passwordHint.text}
                </p>
              )}
            </div>
            <Button
              className="w-full h-12 text-base font-medium"
              onClick={handleSetPassword}
              disabled={loading}
            >
              {loading ? t("auth.signIn.setPasswordLoading") : t("auth.signIn.setPasswordButton")}
            </Button>
          </div>
        ) : mode === "reset" ? (
          /* Password reset */
          <div className="mt-10 space-y-3 animate-page-enter">
            {resetSent ? (
              <div className="rounded-xl border border-border bg-card px-5 py-4 text-sm text-muted-foreground">
                {t("auth.signIn.resetSent")}
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <Input
                    ref={emailInputRef}
                    type="email"
                    placeholder={t("auth.signIn.emailPlaceholder")}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailHint(null); }}
                    onKeyDown={(e) => e.key === "Enter" && handleReset()}
                    className={`h-12 text-base bg-card ${fieldBorder(emailHint)}`}
                  />
                  {emailHint && (
                    <p className="text-[13px] px-1" style={{ color: hintColor(emailHint) }}>
                      {emailHint.text}
                    </p>
                  )}
                </div>
                <Button
                  className="w-full h-12 text-base font-medium"
                  onClick={handleReset}
                  disabled={loading}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {loading ? t("auth.signIn.resetLoading") : t("auth.signIn.resetButton")}
                </Button>
              </>
            )}
            <button
              onClick={() => switchMode("signin")}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("auth.signIn.backToSignIn")}
            </button>
          </div>
        ) : (
          /* Sign in / Sign up */
          <div className="mt-10 space-y-3">
            {/* Name — signup only */}
            {mode === "signup" && (
              <div className="space-y-1">
                <Input
                  autoFocus
                  type="text"
                  placeholder={t("auth.signIn.namePlaceholder")}
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameHint(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSignUp(); }}
                  className={`h-12 text-base bg-card ${fieldBorder(nameHint)}`}
                />
                {nameHint && (
                  <p className="text-[13px] px-1 text-left" style={{ color: hintColor(nameHint) }}>
                    {nameHint.text}
                  </p>
                )}
              </div>
            )}

            {/* Email */}
            <div className="space-y-1">
              <Input
                ref={emailInputRef}
                type="email"
                placeholder={t("auth.signIn.emailPlaceholder")}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailHint(null); }}
                className={`h-12 text-base bg-card ${fieldBorder(emailHint)}`}
              />
              {emailHint && (
                <p className="text-[13px] px-1 text-left" style={{ color: hintColor(emailHint) }}>
                  {emailHint.text}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1">
              <Input
                type="password"
                placeholder={t("auth.signIn.passwordPlaceholder")}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordHint(null); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && mode === "signin") handleSignIn();
                }}
                className={`h-12 text-base bg-card ${fieldBorder(passwordHint)}`}
              />
              {passwordHint && (
                <p className="text-[13px] px-1 text-left" style={{ color: hintColor(passwordHint) }}>
                  {passwordHint.text}
                </p>
              )}
            </div>

            {/* Confirm password — signup only */}
            {mode === "signup" && (
              <div className="space-y-1">
                <Input
                  type="password"
                  placeholder={t("auth.signIn.confirmPasswordPlaceholder")}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setConfirmHint(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSignUp(); }}
                  className={`h-12 text-base bg-card ${fieldBorder(confirmHint)}`}
                />
                {confirmHint && (
                  <p className="text-[13px] px-1 text-left" style={{ color: hintColor(confirmHint) }}>
                    {confirmHint.text}
                  </p>
                )}
              </div>
            )}

            {/* Terms agreement — above the submit button on signup */}
            {mode === "signup" && (
              <label
                htmlFor="agree-terms"
                className="flex items-start gap-3 text-left cursor-pointer select-none pt-1"
              >
                <Checkbox
                  id="agree-terms"
                  checked={agreedToTerms}
                  onCheckedChange={(v) => setAgreedToTerms(v === true)}
                  className="mt-0.5 shrink-0"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {t("auth.terms.agree")}{" "}
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 text-foreground hover:text-primary transition-colors"
                  >
                    {t("auth.terms.termsLink")}
                  </a>{" "}
                  {t("auth.terms.and")}{" "}
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 text-foreground hover:text-primary transition-colors"
                  >
                    {t("auth.terms.privacyLink")}
                  </a>
                  .
                </span>
              </label>
            )}

            <Button
              className="w-full h-12 text-base font-medium"
              onClick={mode === "signup" ? handleSignUp : handleSignIn}
              disabled={loading || (mode === "signup" && !agreedToTerms)}
            >
              {mode === "signup"
                ? loading ? t("auth.signIn.signUpLoading") : t("auth.signIn.signUpButton")
                : loading ? t("auth.signIn.signInLoading") : t("auth.signIn.signInButton")}
            </Button>

            <Button
              variant="outline"
              className="w-full h-12 gap-3 text-base font-medium border-border"
              onClick={mode === "signup" ? handleGoogleSignUp : handleGoogleSignIn}
              disabled={mode === "signup" && !agreedToTerms}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t("auth.signIn.googleButton")}
            </Button>

            <div className="flex items-baseline justify-between pt-1">
              <button
                onClick={() => switchMode(mode === "signup" ? "signin" : "signup")}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {mode === "signup"
                  ? t("auth.signIn.haveAccount")
                  : t("auth.signIn.noAccount")}
              </button>
              {mode === "signin" && (
                <button
                  onClick={() => switchMode("reset")}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("auth.signIn.forgotPassword")}
                </button>
              )}
            </div>

            {mode === "signin" && (
              <button
                onClick={() => switchMode("kid")}
                className="mt-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("auth.kid.entry")}
              </button>
            )}
          </div>
        )}


      </div>

      <AlertDialog open={kidHelpOpen} onOpenChange={setKidHelpOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("auth.kid.helpTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("auth.kid.helpBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>{t("auth.kid.helpOk")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster position="top-center" />
    </div>
  );
};

export default AuthPage;
